import { useQuery } from '@apollo/client/react';
import {
  DimeCard,
  DimeCardAction,
  DimeCardContent,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeChip,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';

import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { useDeniedTooltip } from '../../lib/access/index.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_PRODUCT_TREE,
  type GetProductTreeQueryResult,
  type GetProductTreeQueryVariables,
} from '../../modules/product-trees/operations.js';
import { ProductTreeActiveStatusControl } from '../../modules/product-trees/product-tree-active-status-control.js';
import { ProductTreeProductsTab } from '../../modules/product-trees/product-tree-products-tab.js';

export const Route = createFileRoute('/product-categories/$productTreeId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: ProductTreeDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function ProductTreeDetailPage() {
  const { productTreeId } = Route.useParams();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('product:tree');
  const deniedTooltip = useDeniedTooltip();
  const canManagePriceRules = permissions.includes('pricingEngine:edit');
  const canDeletePriceRules = permissions.includes('pricingEngine:delete');
  const { data, loading, refetch } = useQuery<
    GetProductTreeQueryResult,
    GetProductTreeQueryVariables
  >(GET_PRODUCT_TREE, { variables: { id: productTreeId } });

  const category = data?.productTree;
  useBreadcrumbLabel(productTreeId, category?.name);

  return (
    <div>
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading...</div>
      ) : !category ? (
        <div className="text-sm text-muted-foreground">Category not found.</div>
      ) : (
        <DimeCard borderless className="gap-0 py-0">
          <DimeCardHeader className="border-b px-0 pb-4">
            <DimeCardTitle className="flex items-center gap-2">
              {category.name}
              <DimeChip variant={category.activeFlag ? 'success' : 'destructive'}>
                {category.activeFlag ? 'Active' : 'Inactive'}
              </DimeChip>
              {category.parent && (
                <DimeChip variant="secondary">under {category.parent.name}</DimeChip>
              )}
            </DimeCardTitle>
            <DimeCardDescription>
              {category.description ?? 'Products directly assigned to this category.'}
            </DimeCardDescription>
            <DimeCardAction>
              <ProductTreeActiveStatusControl
                category={category}
                onStatusChanged={() => void refetch()}
                disabled={!canEdit}
                disabledTooltip={canEdit ? undefined : deniedTooltip('product:tree')}
                canManagePriceRules={canManagePriceRules}
                canDeletePriceRules={canDeletePriceRules}
              />
            </DimeCardAction>
          </DimeCardHeader>

          <DimeCardContent className="px-0 pt-4">
            <ProductTreeProductsTab
              productTreeId={category.id}
              categoryName={category.name}
              brandId={category.brand?.id}
            />
          </DimeCardContent>
        </DimeCard>
      )}
    </div>
  );
}
